import React, {useCallback, useMemo, useState} from 'react';

import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {
    LCS_SUBSCRIPTION_TITLES,
} from 'src/modules/bulk-update/update-lcd-credit-analysis/constants';
import {useLcdSubscriptionTrees} from 'src/modules/profile/user/tabs/personalization/lcd-credit-analysis/hooks/useLcdSubscriptionTrees';
import {LcdSubscriptions} from 'src/modules/profile/user/tabs/personalization/lcd-credit-analysis/typedef';
import {iterateLcdTreeCount} from 'src/utils/iterateLcdTreeCount';
import {getTreeStructure} from 'src/utils/mapTree';

import Button from '@pb/kit-component-button';
import CheckBoxTree from '@pb/kit-component-checkbox-tree';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Tab, TabBadge, Tabs} from '@pb/kit-component-tabs-v2';

import type {ChangeTreeType} from '@pb/kit-component-checkbox-tree';
import type {TreeStructure} from '@pb/kit-component-tree-v2';
import type {ProcessBulkAction} from 'src/components/bulk-tool/typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {BulkLcdEnabledRequest} from 'src/openapi/admin/models/BulkLcdEnabledRequest';
import type {RegionSettings} from 'src/openapi/admin/models/RegionSettings';


type Props = {
    usersIds: number[],
    subscribe: boolean,
    onCloseModal: (submit: boolean) => void,
    processAction: ProcessBulkAction<BulkLcdEnabledRequest>,
};

export const UpdateLcdCreditAnalysisSubscriptionModal = ({usersIds, subscribe, onCloseModal, processAction}: Props) => {
    const [activeTab, setActiveTab] = useState<string>(LcdSubscriptions.NEWS);
    const [regionSettings, setRegionSettings] = useState<Record<string, RegionSettings[]>>({
        [LcdSubscriptions.NEWS]: [],
        [LcdSubscriptions.RESEARCH]: [],
    });

    const {data: trees} = useLcdSubscriptionTrees();

    const treeStructures = useMemo<Record<string, TreeStructure>>(() => {
        return trees.reduce((acc, tree) => ({
            ...acc,
            [tree.id]: getTreeStructure(tree).map(region => ({
                ...region,
                expanded: true,
            })),
        }), {} as Record<string, TreeStructure>);
    }, [trees]);

    const getSubscriptionCount = useCallback((subscriptionType: string) => {
        const selected = regionSettings[subscriptionType].reduce((acc, item) => [
            ...acc,
            ...item.subscriptions.map(subscription => ({region: item.region, subscription})),
        ], [] as {region: string, subscription: string}[]);

        if (!selected.length) {
            return 0;
        }

        const treeStructure = treeStructures[subscriptionType];

        const result: string[] = [];
        treeStructure.forEach(treeItem => iterateLcdTreeCount(
            treeItem,
            result,
            selected,
            treeItem.id,
        ));

        return result.length;
    }, [regionSettings, treeStructures]);


    const getSubscriptions = useCallback((subscriptionType: LcdSubscriptions, regionId: string) => {
        const subscriptionPack = regionSettings[subscriptionType];
        const region = subscriptionPack.find(e => e.region === regionId);

        if (region) {
            return region.subscriptions;
        } else {
            return [];
        }
    }, [regionSettings]);

    const onChange = useCallback((
        {checkedIdsList}: ChangeTreeType,
        subscriptionType: LcdSubscriptions,
        regionId: string,
    ) => {
        setRegionSettings(prevState => ({
            ...prevState,
            [subscriptionType]: [
                ...prevState[subscriptionType].filter(region => region.region !== regionId),
                ...checkedIdsList.length ? [{region: regionId, subscriptions: checkedIdsList}] : [],
            ],
        }));
    }, [setRegionSettings]);

    const updateSubscription = useCallback(async (options?: ActionOptions) => {
        return await processAction({
            usersIds,
            regionSettings,
        }, options);
    }, [usersIds, regionSettings, processAction]);

    const canUpdate = useMemo(() => {
        return Object.values(regionSettings).some(region => region.length);
    }, [regionSettings]);

    const newsCount = useMemo(() => getSubscriptionCount(LcdSubscriptions.NEWS), [getSubscriptionCount]);
    const researchCount = useMemo(() => getSubscriptionCount(LcdSubscriptions.RESEARCH), [getSubscriptionCount]);

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {subscribe ? LCS_SUBSCRIPTION_TITLES.SUBSCRIBE : LCS_SUBSCRIPTION_TITLES.UNSUBSCRIBE}
            </DialogHeader>
            <DialogMain style={{padding: '0 24px 24px'}}>
                <Group direction="v" gap="s">
                    <Tabs value={activeTab} onChange={setActiveTab} style={{width: '100%'}}>
                        <Tab
                            value={LcdSubscriptions.NEWS}
                            caption="News"
                            endAdornment={<TabBadge>{newsCount ? `(${newsCount})` : undefined}</TabBadge>}
                        />
                        <Tab
                            value={LcdSubscriptions.RESEARCH}
                            caption="Research"
                            endAdornment={<TabBadge>{researchCount ? `(${researchCount})` : undefined}</TabBadge>}
                        />
                    </Tabs>
                    <p>
                        Select options to {subscribe ? 'add to' : 'remove from'} the user subscription.
                        The subscription to unchecked options will not be changed.
                    </p>
                    <Group justify="space-between">
                        {treeStructures[activeTab]?.map(region => (
                            <div style={{width: 300}} key={region.id}>
                                <CheckBoxTree
                                    data={[region]}
                                    checkedIdsList={getSubscriptions(
                                        activeTab as LcdSubscriptions,
                                        region.id,
                                    )}
                                    onChange={value => onChange(
                                        value,
                                        activeTab as LcdSubscriptions,
                                        region.id,
                                    )}
                                />
                            </div>
                        ))}
                    </Group>
                </Group>
            </DialogMain>
            <BulkActionFooter action={subscribe ? 'Subscribe' : 'Unsubscribe'} onConfirm={updateSubscription} disabled={!canUpdate}>
                <Group>

                    <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>
                        Cancel
                    </Button>
                </Group>
            </BulkActionFooter>
        </>
    );
};
